import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LightningIcon, ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const BG = "https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRef = useRef(false);

  // Shared: actually ask the backend to email a one-time code.
  const sendCode = async (targetEmail) => {
    if (!targetEmail || !targetEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-code", { email: targetEmail.trim() });
      setStep("code");
      if (data.dev_code) {
        setCode(data.dev_code);
        toast.success("Dev mode: code auto-filled", { description: `One-time code: ${data.dev_code}` });
      } else {
        toast.success("Code sent", { description: "Check your inbox for the one-time code." });
      }
    } catch {
      toast.error("Could not send code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Arriving from an emailed "Claim & activate" link: prefill the email AND
  // automatically send a fresh code (so the inbox actually receives one).
  useEffect(() => {
    const invited = searchParams.get("email");
    if (invited && !requestedRef.current) {
      requestedRef.current = true;
      setEmail(invited);
      sendCode(invited);
    }
  }, [searchParams]);

  const requestCode = async (e) => {
    e.preventDefault();
    await sendCode(email);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 8) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email: email.trim(), code, scope: "customer" });
      login(data.token, data.user);
      toast.success("Signed in");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: immersive tech background */}
      <div className="relative hidden lg:block overflow-hidden border-r border-border">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(0,255,102,0.18), rgba(5,5,5,0.9))" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2 font-heading font-black tracking-tighter text-xl">
            <LightningIcon weight="fill" className="text-primary" size={26} />
            D CODE
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">// Dynamic Physical Tags</p>
            <h1 className="font-heading font-black text-5xl xl:text-6xl leading-none tracking-tighter">
              One permanent code.<br />
              <span className="text-primary">An editable destination</span><br />
              forever.
            </h1>
            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              Pins, cards, t-shirts carry a code that never changes. Where it sends people is yours to rewire anytime.
            </p>
          </div>
          <p className="font-mono text-[11px] text-white/30 tracking-widest">POPIA / GDPR-AWARE · HASHED SCAN LOGS</p>
        </div>
      </div>

      {/* Right: magic-link form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2 font-heading font-black tracking-tighter text-xl mb-10">
            <LightningIcon weight="fill" className="text-primary" size={24} />
            D CODE
          </div>

          {step === "email" ? (
            <form onSubmit={requestCode} className="space-y-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Sign in</p>
                <h2 className="font-heading font-black text-4xl tracking-tighter leading-none">Magic link access</h2>
                <p className="text-white/50 mt-4">No passwords. We email you a one-time code.</p>
              </div>
              <div className="space-y-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Email address</label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@studio.co"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 bg-secondary/40 border-border font-mono text-base focus-visible:ring-primary"
                />
              </div>
              <Button
                data-testid="login-request-code-btn"
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full h-14 bg-primary text-primary-foreground font-bold tracking-wide hover:bg-primary/90 transition-colors group"
              >
                {busy ? "SENDING…" : "EMAIL ME A CODE"}
                <ArrowRightIcon className="ml-2 group-hover:translate-x-1 transition-transform" weight="bold" />
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-8">
              <div>
                <button
                  type="button"
                  data-testid="login-back-btn"
                  onClick={() => { setStep("email"); setCode(""); }}
                  className="flex items-center gap-1.5 font-mono text-xs text-white/50 hover:text-primary transition-colors mb-6"
                >
                  <ArrowLeftIcon weight="bold" /> BACK
                </button>
                <h2 className="font-heading font-black text-4xl tracking-tighter leading-none">Enter the code</h2>
                <p className="text-white/50 mt-4 break-all">
                  Sent to <span className="text-white font-mono">{email}</span>
                </p>
              </div>
              <div data-testid="login-otp-wrap" className="flex justify-center py-2">
                <InputOTP maxLength={8} value={code} onChange={setCode} containerClassName="gap-1.5">
                  <InputOTPGroup className="gap-1.5">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-14 w-11 text-lg font-mono border-border bg-secondary/40 data-[active=true]:ring-primary data-[active=true]:border-primary"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                data-testid="login-verify-btn"
                type="submit"
                disabled={busy || code.length !== 8}
                className="w-full h-14 bg-primary text-primary-foreground font-bold tracking-wide hover:bg-primary/90 transition-colors"
              >
                {busy ? "VERIFYING…" : "VERIFY & ENTER"}
              </Button>
              <p className="text-center font-mono text-[11px] text-white/30 tracking-wider">
                Code expires in 10 minutes · single use
              </p>
              <p className="text-center font-mono text-[11px] text-white/40">
                Didn't get it?{" "}
                <button type="button" data-testid="login-resend-btn" onClick={() => sendCode(email)} disabled={busy} className="text-primary hover:underline disabled:opacity-50">
                  Resend code
                </button>
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
